import * as appEditorAuthentication from './translations/app-editor.authentication.json';
import * as appEditorHelp from './translations/app-editor.help.json';
import * as appEditorIntegrations from './translations/app-editor.integrations.json';
import * as appEditorTheme from './translations/app-editor.theme.json';
import * as appEditorNavigation from './translations/app-editor.navigation.json';
import * as appEditorVariables from './translations/app-editor.variables.json';
import * as appEditorVariableProperties from './translations/app-editor.variable-properties.json';
import * as appEditorTemplateEditor from './translations/app-editor.template-editor.json';
import * as appEditorCanvas from './translations/app-editor.canvas.json';
import * as appEditorCanvasContextMenu from './translations/app-editor.canvas-context-menu.json';
import * as appEditorHeader from './translations/app-editor.header.json';
import * as appEditorPublish from './translations/app-editor.publish.json';
import * as appBuildPage from './translations/app-build.page.json';
import * as appBuildHeader from './translations/app-build.header.json';
import * as appBuildHistory from './translations/app-build.history.json';
import * as appEditorMarketplaceSearch from './translations/app-editor.marketplace-search.json';
import * as appEditorMarketplaceDetail from './translations/app-editor.marketplace-detail.json';
import * as buildUi from './translations/build-ui.json';
import * as commonSignedOut from './translations/common.signed-out.json';

export type TranslationKey<T> = T extends 'app-editor.canvas'
  ? keyof typeof appEditorCanvas
  : T extends 'app-editor.authentication'
    ? keyof typeof appEditorAuthentication
    : T extends 'app-editor.canvas-context-menu'
      ? keyof typeof appEditorCanvasContextMenu
      : T extends 'app-editor.header'
        ? keyof typeof appEditorHeader
        : T extends 'app-editor.help'
          ? keyof typeof appEditorHelp
          : T extends 'app-editor.integrations'
            ? keyof typeof appEditorIntegrations
            : T extends 'app-editor.marketplace-search'
              ? keyof typeof appEditorMarketplaceSearch
              : T extends 'app-editor.marketplace-detail'
                ? keyof typeof appEditorMarketplaceDetail
                : T extends 'app-editor.navigation'
                  ? keyof typeof appEditorNavigation
                  : T extends 'app-editor.publish'
                    ? keyof typeof appEditorPublish
                    : T extends 'app-editor.template-editor'
                      ? keyof typeof appEditorTemplateEditor
                      : T extends 'app-editor.theme'
                        ? keyof typeof appEditorTheme
                        : T extends 'app-editor.variables'
                          ? keyof typeof appEditorVariables
                          : T extends 'app-editor.variable-properties'
                            ? keyof typeof appEditorVariableProperties
                            : T extends 'app-build.page'
                              ? keyof typeof appBuildPage
                              : T extends 'app-build.header'
                                ? keyof typeof appBuildHeader
                                : T extends 'app-build.history'
                                  ? keyof typeof appBuildHistory
                                  : T extends 'build-ui'
                                    ? keyof typeof buildUi
                                    : T extends 'common.signed-out'
                                      ? keyof typeof commonSignedOut
                                      : never;

export const supportedLanguages = [
  { key: 'ar', label: 'العربية', info: 'Arabic', rtl: true },
  { key: 'bg', label: 'Български', info: 'Bulgarian' },
  { key: 'cs', label: 'Čeština', info: 'Czech' },
  { key: 'da', label: 'Dansk', info: 'Danish' },
  { key: 'de', label: 'Deutsch', info: 'German' },
  { key: 'el', label: 'Ελληνικά', info: 'Greek' },
  { key: 'en', label: 'English', info: 'English' },
  { key: 'es', label: 'Español', info: 'Spanish' },
  { key: 'fi', label: 'Suomi', info: 'Finnish' },
  { key: 'fr', label: 'Français', info: 'French' },
  { key: 'he', label: 'עברית', info: 'Hebrew', rtl: true },
  { key: 'hr', label: 'Hrvatski', info: 'Croatian' },
  { key: 'hu', label: 'Magyar', info: 'Hungarian' },
  { key: 'it', label: 'Italiano', info: 'Italian' },
  { key: 'ja', label: '日本語', info: 'Japanese' },
  { key: 'kk', label: 'Қазақ тілі', info: 'Kazakh' },
  { key: 'ko', label: '한국어', info: 'Korean' },
  { key: 'ms', label: 'Bahasa Melayu', info: 'Malay' },
  { key: 'nl', label: 'Nederlands', info: 'Dutch' },
  { key: 'no', label: 'Norsk', info: 'Norwegian' },
  { key: 'pl', label: 'Polski', info: 'Polish' },
  { key: 'pt', label: 'Português', info: 'Portuguese' },
  { key: 'ro', label: 'Română', info: 'Romanian' },
  { key: 'ru', label: 'Русский', info: 'Russian' },
  { key: 'sh', label: 'Srpsko-hrvatski', info: 'Serbian/Croatian' },
  { key: 'sk', label: 'Slovenčina', info: 'Slovak' },
  { key: 'sl', label: 'Slovenščina', info: 'Slovenian' },
  { key: 'sv', label: 'Svenska', info: 'Swedish' },
  { key: 'th', label: 'ไทย', info: 'Thai' },
  { key: 'tr', label: 'Türkçe', info: 'Turkish' },
  { key: 'uk', label: 'Українська', info: 'Ukrainian' },
  { key: 'vi', label: 'Tiếng Việt', info: 'Vietnamese' },
  { key: 'zh_CN', label: '中文 (简体)', info: 'Chinese (Simplified)' },
  { key: 'zh_TW', label: '中文 (繁體)', info: 'Chinese (Traditional)' },
  { key: 'en_US_sappsd', label: 'Pseudo', info: 'For testing' },
] as const;

export type SupportedLanguage = (typeof supportedLanguages)[number]['key'];

export const supportedLanguageKeys: SupportedLanguage[] = supportedLanguages.map(({ key }) => key);

export const DEFAULT_INTERFACE_LANGUAGE: SupportedLanguage = 'en';
